import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { api } from '../lib/api';
import { ExternalLink, Film, Instagram, Youtube, User } from 'lucide-react';

const nfmt = (v) => (v == null ? '—' : Number(v).toLocaleString());
const fmtDate = (v) => {
  if (!v) return null;
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return null;
  return d.toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
};

function TikTokIcon(props) {
  return (
    <svg viewBox="0 0 24 24" fill="currentColor" {...props}>
      <path d="M19.6 6.8a5.6 5.6 0 0 1-3.3-1.1V15a5.4 5.4 0 1 1-5.4-5.4c.3 0 .6 0 .9.1v2.6a2.8 2.8 0 1 0 2 2.7V2h2.5a3.4 3.4 0 0 0 3.3 3v1.8Z" />
    </svg>
  );
}
const PLATFORM_ICON = { instagram: Instagram, tiktok: TikTokIcon, youtube: Youtube, other: Film };
const TOTAL_METRICS = [
  { k: 'views', l: 'views' }, { k: 'likes', l: 'likes' }, { k: 'comments', l: 'comments' },
  { k: 'saves', l: 'saves' }, { k: 'shares', l: 'shares' },
];

const PublicContentReport = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(false);

  useEffect(() => {
    api.get(`/public/content/${token}`).then(({ data }) => setData(data)).catch(() => setError(true));
  }, [token]);

  if (error) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400" data-testid="public-content-error">this report link is no longer valid</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center text-sm text-slate-400">loading…</div>;

  const { talent, brand, items, totals } = data;

  return (
    <div className="min-h-screen bg-gradient-to-br from-pink-50 via-white to-lilac-50 p-6 lg:p-10" data-testid="public-content-page">
      <div className="max-w-4xl mx-auto">
        <header className="flex items-center gap-3 mb-6">
          <span className="w-11 h-11 rounded-full bg-white border border-pink-200 flex items-center justify-center text-pink-400 text-lg overflow-hidden shrink-0">
            {talent?.picture ? <img src={talent.picture} alt="" className="w-full h-full object-cover" /> : <User className="w-5 h-5" />}
          </span>
          <div className="min-w-0">
            <h1 className="text-2xl font-semibold text-slate-900 truncate">{talent?.name || 'talent'} × {brand?.name || 'brand'}</h1>
            <p className="text-sm text-slate-500 flex items-center gap-1.5">
              {brand?.logo && <span>{brand.logo}</span>} content report · view-only
            </p>
          </div>
        </header>

        <div className="bg-white border border-pink-100 rounded-2xl p-4 mb-6 flex items-center gap-6 flex-wrap text-sm" data-testid="public-content-totals">
          <div>
            <div className="font-semibold text-slate-900">{items.length}</div>
            <div className="text-slate-400 text-xs">post{items.length === 1 ? '' : 's'}</div>
          </div>
          {TOTAL_METRICS.map((m) => (
            <div key={m.k} data-testid={`public-content-total-${m.k}`}>
              <div className="font-semibold text-slate-900 tabular-nums">{nfmt(totals[m.k])}</div>
              <div className="text-slate-400 text-xs">{m.l}</div>
            </div>
          ))}
        </div>

        <div className="space-y-2">
          {items.length === 0 ? (
            <div className="bg-white border border-dashed border-pink-200 rounded-2xl p-10 text-center text-sm text-slate-400">no content logged for this brand yet</div>
          ) : items.map((c) => {
            const PlatformIcon = PLATFORM_ICON[c.platform] || Film;
            const href = c.link && /^https?:\/\//i.test(c.link) ? c.link : (c.link ? `https://${c.link}` : '');
            const img = c.screenshot_url;
            return (
              <div key={c.id} className="bg-white border border-pink-100 rounded-2xl p-3 flex items-center gap-3" data-testid={`public-content-item-${c.id}`}>
                {img ? (
                  <img src={img} alt="" className="w-12 h-12 rounded-md object-cover border border-slate-200 shrink-0" />
                ) : (
                  <div className="w-12 h-12 rounded-md bg-slate-50 border border-slate-100 flex items-center justify-center text-slate-300 shrink-0">
                    <PlatformIcon className="w-4 h-4" />
                  </div>
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    {fmtDate(c.posted_at) && <span className="text-xs text-slate-400">{fmtDate(c.posted_at)}</span>}
                    {href ? (
                      <a href={href} target="_blank" rel="noopener noreferrer" className="text-sm text-pink-600 hover:text-pink-700 truncate max-w-[320px] inline-flex items-center gap-1">
                        {href} <ExternalLink className="w-3 h-3 shrink-0" />
                      </a>
                    ) : (
                      <span className="text-sm text-slate-400 italic">no link</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3 text-xs text-slate-500 mt-1 flex-wrap">
                    {TOTAL_METRICS.map((m) => c[m.k] != null && (
                      <span key={m.k} className="tabular-nums">{nfmt(c[m.k])} {m.l}</span>
                    ))}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        <p className="text-center text-[11px] text-slate-400 mt-8">shared read-only content report · lve social</p>
      </div>
    </div>
  );
};

export default PublicContentReport;
